"use client";

import { useEffect, useRef, useState } from "react";
import { APEX_ORIGIN } from "@/lib/zones/app-routes";

export interface CompanyLogoProps {
	company: string | null;
	domain: string | null;
	logoR2Key?: string | null;
	/**
	 * Confidence score (0–1) behind `logoR2Key`, from `companies.logo_confidence`.
	 * `undefined`/`null` (callers that haven't threaded it through yet) is now
	 * treated as NOT high-confidence — it demotes the cached R2 asset below
	 * logo.dev, the same as an explicit low score. Only an EXPLICIT score at or
	 * above {@link HIGH_CONFIDENCE_THRESHOLD} promotes `logoR2Key` to the first
	 * tier. This flips the old default (undefined ⇒ trusted), which silently
	 * promoted every low-confidence favicon scrape at every call site that
	 * hadn't threaded the prop through yet — i.e. almost all of them. See the
	 * tier-ladder comment on {@link buildLadder}.
	 */
	logoConfidence?: number | null;
	size?: number;
	className?: string;
	onLogoError?: () => void;
	/** Set to true for the above-fold LCP image to preload eagerly. */
	priority?: boolean;
}

const PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_LOGO_DEV_PUBLISHABLE_KEY ?? "";

// `r2` used to be a single unconditional first tier. It's split into
// `r2_high`/`r2_low` because ~93% of cached R2 assets are low-confidence
// Google favicon scrapes (`logo_source='favicon'`, confidence 0.4) that
// visually shadow a better logo.dev brand image — see `buildLadder` below.
type LogoTier = "r2_high" | "logodev" | "r2_low" | "favicon" | "monogram";

const HIGH_CONFIDENCE_THRESHOLD = 0.7;

function isHighConfidence(logoConfidence: number | null | undefined): boolean {
	// undefined used to mean "trust the cached asset", which silently promoted
	// every low-confidence favicon scrape at every call site that hadn't
	// threaded the prop through — i.e. all of them. Default to the safe tier
	// instead.
	return logoConfidence != null && logoConfidence >= HIGH_CONFIDENCE_THRESHOLD;
}

/**
 * Ordered fallback ladder for the tiers actually available for this company:
 * high-confidence R2 → logo.dev → low-confidence R2 favicon → Google favicon
 * → monogram. `logoR2Key`'s cached asset appears AT MOST ONCE — promoted to
 * `r2_high` only when confidence is EXPLICITLY at/above the threshold, or
 * demoted to `r2_low` (behind logo.dev) when it's a known low-confidence
 * scrape OR confidence is unknown/not threaded through yet — never both,
 * since retrying the identical asset under a different tier name would just
 * repeat the same failure.
 */
function buildLadder(
	hasR2Key: boolean,
	domain: string | null,
	logoConfidence: number | null | undefined,
): LogoTier[] {
	const highConfidence = isHighConfidence(logoConfidence);
	const ladder: LogoTier[] = [];
	if (hasR2Key && highConfidence) ladder.push("r2_high");
	if (domain && PUBLISHABLE_KEY) ladder.push("logodev");
	if (hasR2Key && !highConfidence) ladder.push("r2_low");
	if (domain) ladder.push("favicon");
	ladder.push("monogram");
	return ladder;
}

function buildLogoUrl(domain: string, size: number): string {
	const params = new URLSearchParams({
		token: PUBLISHABLE_KEY,
		size: String(size * 2), // 2x for retina
		format: "webp",
		retina: "true",
	});
	return `https://img.logo.dev/${domain}?${params.toString()}`;
}

function buildR2LogoUrl(key: string): string {
	const safe = key
		.split("/")
		.map((s) => encodeURIComponent(s))
		.join("/");
	// Seeker zone (apps/app) has no /api/logos route; apex serves R2.
	return `${APEX_ORIGIN}/api/logos/${safe}`;
}

export function buildFaviconUrl(domain: string, size: number): string {
	return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size * 2}`;
}

function monogramFor(name: string): string {
	const trimmed = name.trim();
	if (!trimmed) return "?";
	// Up to 2 initials from the first two words for a richer avatar
	const words = trimmed.split(/\s+/).filter(Boolean);
	const first = words[0]?.codePointAt(0);
	const second = words[1]?.codePointAt(0);
	if (first === undefined) return "?";
	const a = String.fromCodePoint(first).toUpperCase();
	const b =
		second !== undefined ? String.fromCodePoint(second).toUpperCase() : "";
	return a + b;
}

// Deterministic background tint per company so listings don't flicker on every
// rerender and the same company gets a consistent monogram color.
function tintFor(slug: string): string {
	let hash = 0;
	for (let i = 0; i < slug.length; i++) {
		hash = (hash * 31 + slug.charCodeAt(i)) | 0;
	}
	// `-900` text, not `-700`. The monogram is real text, and these badges are
	// rendered under a `grayscale` filter on the homepage logo strip, which
	// collapses a tint pair toward mid-grey: axe measured the `-700` pairs at
	// 2.59:1 and 1.46:1 against a 4.5:1 requirement. `-900` on `-100` clears AA
	// both in colour and after desaturation, which is what makes it safe to
	// reuse this component anywhere a filter might be applied.
	//
	// Each pair also carries an explicit dark-mode counterpart. Bare
	// `bg-*-100 text-*-900` inverts to near-black-on-near-white in dark mode —
	// the 2026-06-19 linktree incident, see src/components/CLAUDE.md.
	const palette = [
		"bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100",
		"bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
		"bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
		"bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100",
		"bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-100",
		"bg-cyan-100 text-cyan-900 dark:bg-cyan-950 dark:text-cyan-100",
		"bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-100",
		"bg-lime-100 text-lime-900 dark:bg-lime-950 dark:text-lime-100",
	];
	return palette[Math.abs(hash) % palette.length];
}

function initialTier(
	logoR2Key: string | null | undefined,
	domain: string | null,
	logoConfidence: number | null | undefined,
): LogoTier {
	return buildLadder(!!logoR2Key, domain, logoConfidence)[0];
}

/**
 * True when a loaded image is too small for the box it has to fill.
 *
 * `nextTier` only ever ran on an ERROR, and the favicon tier does not error:
 * `https://www.google.com/s2/favicons?...&sz=72` answers 200 with whatever it
 * has cached, which for most domains is a 16px icon regardless of the `sz`
 * asked for. The browser then stretches 16px across a 36px box and the result
 * is the smeared logo every Hunt match card shipped with. A crisp monogram is
 * better than a smeared favicon, so a too-small image is treated as a failed
 * tier rather than a successful one.
 *
 * The two-thirds threshold is chosen to reject the 16px case while keeping the
 * common 32px favicon, which upscales to 36px without visible damage. `size`
 * is the CSS box, not the `sz` we requested — what matters is the ratio the
 * user actually sees.
 *
 * (The root cause underneath this is separate and worth fixing too: the
 * `logodev` tier is never reachable in production because
 * `NEXT_PUBLIC_LOGO_DEV_PUBLISHABLE_KEY` has never been configured for any
 * worker — `scripts/secrets/secret-inventory.json` records `declaredIn: []` —
 * so every company falls through to Google. This guard makes the fallback
 * honest either way.)
 */
export function isTooLowResolution(
	naturalWidth: number,
	size: number,
): boolean {
	return naturalWidth > 0 && naturalWidth * 1.5 < size;
}

function nextTier(
	current: LogoTier,
	logoR2Key: string | null | undefined,
	domain: string | null,
	logoConfidence: number | null | undefined,
): LogoTier {
	const ladder = buildLadder(!!logoR2Key, domain, logoConfidence);
	const idx = ladder.indexOf(current);
	if (idx === -1 || idx === ladder.length - 1) return "monogram";
	return ladder[idx + 1];
}

export function CompanyLogo({
	company,
	domain,
	logoR2Key,
	logoConfidence,
	size = 32,
	className = "",
	onLogoError,
	priority = false,
}: CompanyLogoProps) {
	const [tier, setTier] = useState<LogoTier>(() =>
		initialTier(logoR2Key, domain, logoConfidence),
	);
	// Whether the current tier's <img> has actually painted. Until it does — and
	// for cross-origin, below-the-fold logos that can take seconds — the monogram
	// shows underneath instead of a blank white box (the QA "empty logo" report).
	const [loaded, setLoaded] = useState(false);
	const imgRef = useRef<HTMLImageElement>(null);
	const safeName = company ?? "";
	const tint = tintFor(safeName.toLowerCase());

	const baseClasses = `inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md ${className}`;
	const style = { width: size, height: size };

	let src: string | null = null;
	if ((tier === "r2_high" || tier === "r2_low") && logoR2Key) {
		src = buildR2LogoUrl(logoR2Key);
	} else if (tier === "logodev" && domain && PUBLISHABLE_KEY) {
		src = buildLogoUrl(domain, size);
	} else if (tier === "favicon" && domain) {
		src = buildFaviconUrl(domain, size);
	}

	// A cached image can finish loading — OR already have failed — before React
	// attaches `onLoad`/`onError` (e.g. an ad/tracker blocker kills the
	// `img.logo.dev` request synchronously, before hydration). Without the
	// `naturalWidth === 0` branch here, a row that failed pre-hydration got
	// stuck on the failed tier forever instead of cascading like a live
	// `onError` does.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `src` is the effective dependency (derived from `tier`); re-run when it changes. `size` is stable per call site.
	useEffect(() => {
		setLoaded(false);
		const el = imgRef.current;
		if (!el?.complete) return;
		if (el.naturalWidth > 0 && !isTooLowResolution(el.naturalWidth, size)) {
			setLoaded(true);
			return;
		}
		const next = nextTier(tier, logoR2Key, domain, logoConfidence);
		setTier(next);
		if (next === "monogram") onLogoError?.();
	}, [src]);

	const initials = monogramFor(safeName);
	const fontSize = initials.length > 1 ? "text-xs" : "text-sm";

	// No image source at all → the monogram IS the logo (unchanged).
	if (!src) {
		return (
			<span
				aria-hidden="true"
				data-logo-tier={tier}
				data-logo-loaded={loaded ? "true" : "false"}
				className={`${baseClasses} ${tint} ${fontSize} font-semibold ring-1 ring-inset ring-current/10`}
				style={style}
			>
				{initials}
			</span>
		);
	}

	// Image tier: monogram placeholder underneath, the logo layered on top and
	// revealed (white-backed, as before) only once it paints. If every image
	// tier fails, `nextTier` lands on "monogram" → `src` becomes null → the
	// branch above renders the monogram as the final fallback.
	return (
		<span
			data-logo-tier={tier}
			data-logo-loaded={loaded ? "true" : "false"}
			className={`${baseClasses} relative ${loaded ? "" : `${tint} ${fontSize} font-semibold ring-1 ring-inset ring-current/10`}`}
			style={style}
		>
			{!loaded ? <span aria-hidden="true">{initials}</span> : null}
			{/* biome-ignore lint/performance/noImgElement: 32px third-party logos don't benefit from next/image and don't need remotePatterns config */}
			<img
				ref={imgRef}
				src={src}
				alt={company ? `${company} logo` : ""}
				width={size}
				height={size}
				loading={priority ? "eager" : "lazy"}
				fetchPriority={priority ? "high" : undefined}
				decoding={priority ? "sync" : "async"}
				className={`absolute inset-0 h-full w-full bg-white object-contain transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`}
				onLoad={(e) => {
					// A 200 is not a usable logo. See `isTooLowResolution`: the
					// favicon tier answers with a 16px icon and no error, so
					// "loaded" has to mean "loaded at a resolution worth showing"
					// or the cascade never runs for the one tier that needs it.
					if (isTooLowResolution(e.currentTarget.naturalWidth, size)) {
						const next = nextTier(tier, logoR2Key, domain, logoConfidence);
						setTier(next);
						if (next === "monogram") onLogoError?.();
						return;
					}
					setLoaded(true);
				}}
				onError={() => {
					const next = nextTier(tier, logoR2Key, domain, logoConfidence);
					setTier(next);
					if (next === "monogram") onLogoError?.();
				}}
			/>
		</span>
	);
}
